import React from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material'
import { Edit, AccountBalance, Upload } from '@mui/icons-material'

const currencyBRL = (n) =>
    (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const AccountCard = ({ account, onEdit, onViewEntries, onImport }) => {
    return (
        <Card
            sx={{
                height: '100%',
                border: '1px solid #E2E8F0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${account.color || '#3B82F6'}`,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                },
                cursor: 'pointer',
            }}
            onClick={() => onViewEntries(account.id)}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: '#1E293B', fontWeight: 600, mb: 0.25 }}
                            noWrap
                        >
                            {account.nickname || account.bank_name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                {account.bank_name}
                            </Typography>
                            <Chip
                                label={account.data_source === 'automatic' ? 'Integrada' : 'Manual'}
                                size="small"
                                sx={{
                                    bgcolor: account.data_source === 'automatic' ? '#22C55E14' : '#3B82F614',
                                    color: account.data_source === 'automatic' ? '#22C55E' : '#3B82F6',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    border: `1px solid ${account.data_source === 'automatic' ? '#22C55E30' : '#3B82F630'}`,
                                }}
                            />
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(account)
                        }}
                        sx={{ ml: 1 }}
                    >
                        <Edit sx={{ fontSize: 18, color: '#94A3B8' }} />
                    </IconButton>
                </Box>

                <Typography
                    variant="caption"
                    sx={{ color: '#64748B', display: 'block', mt: 0.5, fontFamily: 'monospace' }}
                >
                    Ag: {account.bank_branch}
                    {account.bank_branch_digit ? `-${account.bank_branch_digit}` : ''} / Cc:{' '}
                    {account.bank_account}
                    {account.bank_account_digit ? `-${account.bank_account_digit}` : ''}
                </Typography>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: account.current_balance >= 0 ? '#1E293B' : '#EF4444',
                        mt: 2,
                        mb: 1,
                    }}
                >
                    {currencyBRL(account.current_balance)}
                </Typography>

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Chip
                        label={`${account.entries_count || 0} lançamentos`}
                        size="small"
                        sx={{
                            bgcolor: '#F1F5F9',
                            color: '#64748B',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 26,
                        }}
                    />
                    <Chip
                        label={account.account_type === 'savings' ? 'Poupança' : 'Corrente'}
                        size="small"
                        sx={{
                            bgcolor: `${account.color || '#3B82F6'}14`,
                            color: account.color || '#3B82F6',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                            border: `1px solid ${account.color || '#3B82F6'}30`,
                        }}
                    />
                </Box>

                <Box display="flex" gap={1} mt={2}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AccountBalance sx={{ fontSize: 16 }} />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onViewEntries(account.id)
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 1.5,
                            borderColor: '#E2E8F0',
                            color: '#64748B',
                            flex: 1,
                            '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                        }}
                    >
                        Extrato
                    </Button>
                    <Tooltip
                        title={account.data_source === 'automatic' ? 'Conta integrada não permite importação manual' : ''}
                        arrow
                    >
                        <span style={{ flex: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Upload sx={{ fontSize: 16 }} />}
                                disabled={account.data_source === 'automatic'}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onImport(account.id)
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    borderColor: '#E2E8F0',
                                    color: '#64748B',
                                    flex: 1,
                                    width: '100%',
                                    '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                                }}
                            >
                                Importar
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    )
}

export default AccountCard
